import React from 'react';
import { has, mapValues, partition, pick, keys } from 'lodash-es';

import { DEFAULT_REPORT } from './constants';
import { isSameRefNo, isUnified } from '../../../utils/helpers';
import {
  PANEL_VERSIONS,
  PUBLIC_PANEL_VERSIONS,
  ADVANCED_METRICS_V2_MAP,
  ADVANCED_METRICS_V2,
  COHORTS,
  COHORTS_BY_PANEL,
  GROUPED_COHORTS_BY_PANEL,
  EXISTING_CROSS_APP_METRICS_PANELS,
} from '../../../utils/advanced-metrics';

const prepareInitialEntities = ({ entities, first_entities }) => {
  const entitiesExtraParams = first_entities?.reduce((result, entity) => ({
    ...result,
    [entity.ref_no]: entity,
  }), {});

  return entities.map(entity => ({
    ...entity,
    ...(entitiesExtraParams?.[entity.ref_no]),
    ...(entity.store && {
      kind: isUnified(entity.store) ? `unified_${entity.kind}` : entity.kind,
    }),
  }));
};

export const makeInitialReport = (rawReport, {
  redefinedName,
  dataType,
}) => {
  let initialReport = DEFAULT_REPORT;

  if (rawReport) {
    const reportFields = Object.keys(initialReport[dataType]);

    initialReport = mapValues({
      ...initialReport,
      [dataType]: {
        ...initialReport[dataType],
        ...pick(rawReport, reportFields),
        entities: prepareInitialEntities(rawReport),
        ...(dataType === 'advanced'
          ? {
            cross_apps_pairs_decorated: rawReport.cross_app_pairs_decorated || {},
            cross_apps_pairs: keys(rawReport.cross_app_pairs_decorated).reduce((result, key) => ({
              ...result,
              [key]: rawReport.cross_app_pairs_decorated[key].map(item => item.unified_app_id),
            }), {}),
          }
          : {}),
      },
    }, report => ({ ...report, id: rawReport.id }));
  }

  if (redefinedName) {
    initialReport = mapValues(initialReport, report => ({ ...report, name: redefinedName }));
  }

  return initialReport;
};


export const csvOnlyBaseDataPointsOptions = [
  { value: 'engagement', text: 'Engagement' },
  { value: 'avg_revenue_per_user', text: 'Avg. revenue per user' },
  { value: 'sessions_per_user', text: 'Sessions per user' },
  { value: 'total_revenue', text: 'Total revenue' },
  { value: 'avg_session_len', text: 'Avg. session length' },
  { value: 'download_revenue', text: 'Download Revenue' },
  { value: 'review_keywords', text: 'Review keywords' },
  { value: 'daily_app_category_ranks', text: 'Store rank' },
  { value: 'daily_app_star_ratings', text: 'Avg. star rating' },
  { value: 'review_sentiments', text: 'Overall sentiment' },
  { value: 'review_intents', text: 'Review volume' },
];

export const baseDataPointsOptions = [
  { value: 'downloads', text: 'Downloads' },
  { value: 'mau', text: 'Monthly active users' },
  { value: 'iap_revenue', text: 'IAP revenue' },
  { value: 'sessions', text: 'Total sessions' },
  { value: 'dau', text: 'Daily active users' },
  { value: 'total_time', text: 'Total time spent in app' },
  ...csvOnlyBaseDataPointsOptions.map(item => ({
    ...item,
    text: <>{item.text}<sup>*</sup></>,
    csvOnly: true,
  })),
];

export const notAllowMetricFor = (panelVersion, metric) => !EXISTING_CROSS_APP_METRICS_PANELS.includes(panelVersion) && metric.startsWith('cross_app');

export const advancedDataPointsOptions = (panelVersion) => {
  const advancedMetrics = ADVANCED_METRICS_V2_MAP[panelVersion] || [];
  return advancedMetrics.map(key => ({
    value: key,
    text: ADVANCED_METRICS_V2[key],
    disabled: notAllowMetricFor(panelVersion, key),
  }));
};

export const userSegmentsOptions = (panelVersion, disabledNotCrossApp = false) => {
  const cohorts = COHORTS_BY_PANEL[panelVersion] || [];
  return cohorts.map(key => ({
    value: key,
    text: COHORTS[key],
    disabled: disabledNotCrossApp && key !== 'all_users',
  }));
};

export const groupedSegmentsOptions = (panelVersion, disabledNotCrossApp = false) => {
  const cohortsSegments = GROUPED_COHORTS_BY_PANEL[panelVersion] || {};
  return mapValues(cohortsSegments, (segments, key) => segments.map(segment => ({
    value: segment,
    text: key === 'ai_segments' ? COHORTS[segment].replace(' Users', '') : COHORTS[segment],
    disabled: disabledNotCrossApp && segment !== 'all_users',
  })));
};

export const csvOnlyDataPoints = panelVersion => [
  ...csvOnlyBaseDataPointsOptions,
  ...advancedDataPointsOptions(panelVersion),
].map(item => item.value);

export const panelsVersionsOptions = Object.keys(PANEL_VERSIONS).map(key => ({
  label: key,
  value: PANEL_VERSIONS[key],
}));

export const panelsVersionsFor = versions => versions.map(version => ({
  label: version === 'US' ? 'United States' : version,
  value: PUBLIC_PANEL_VERSIONS[version],
}));

export const groupReportEntities = (report, { advancedTrialCompanyId } = {}) => {
  let reportEntities = report.entities || [];
  let excludedEntities = [];

  if (report.data_type === 'advanced') {
    [reportEntities, excludedEntities] = partition(reportEntities, (entity) => {
      if (advancedTrialCompanyId && !isSameRefNo(entity.parent_company_id, advancedTrialCompanyId)) {
        return false;
      }

      return entity.panels?.includes(report.panel_version);
    });
  }

  if (report.data_type === 'mpi') {
    [reportEntities, excludedEntities] = partition(reportEntities, entity => (
      entity.kind === 'company' && (!has(entity, 'has_mpi') || entity.has_mpi)));
  }

  return {
    reportEntities,
    excludedEntities,
  };
};
